import React, { useEffect } from 'react';
import Carousel from 'react-material-ui-carousel';
import { Stack, Typography } from '@mui/material';
import { VisibilityOffOutlined } from '@mui/icons-material';
import { Dialog } from '../Dialog';
import { CreativeFile, CreativeType } from 'types';

type GalleryProps = {
  url: string;
  isOpened: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  selectedCreative: number;
  creatives?: (CreativeFile & { isPreviewInvalid: boolean })[];
  creativesOverview?: {
    url: string;
    originalFileName: string;
    creativeType: CreativeType | null;
    isPreviewInvalid: boolean;
  }[];
};

const NoPreview = () => (
  <>
    <VisibilityOffOutlined color="error" sx={({ spacing }) => ({ fontSize: spacing(50) })} />
    <Typography color="error" variant={'h3'}>
      Keine Vorschau verfügbar
    </Typography>
  </>
);

export const Gallery = ({
  isOpened,
  url,
  onClose,
  creatives,
  creativesOverview,
  selectedCreative,
  onNext,
  onPrev,
}: GalleryProps) => {
  const title = `Hochgeladene Creatives für\n${url}`;
  const content = (
    <Carousel
      height={688}
      autoPlay={false}
      animation="slide"
      indicators={false}
      fullHeightHover={false}
      navButtonsAlwaysVisible
      index={selectedCreative}
      sx={{ backgroundColor: 'grey.800', mx: -5, mb: -5 }}
      next={onNext}
      prev={onPrev}
    >
      {creatives
        ? creatives?.map(({ file, creativeType, url, originalFileName, isPreviewInvalid }, index) => (
            <Stack key={index} height="100%" p={5}>
              <Stack alignItems="center" justifyContent="center" flexGrow={1}>
                {file?.type?.toLowerCase().includes('video') || creativeType?.id?.toLowerCase().includes('video') ? (
                  isPreviewInvalid ? (
                    <NoPreview />
                  ) : (
                    <video width="85%" controls>
                      <source src={url || URL.createObjectURL(file)} />
                    </video>
                  )
                ) : (
                  <img alt={originalFileName || file.name} src={url || URL.createObjectURL(file)} />
                )}
              </Stack>
              <Stack textAlign="center">
                <Typography variant="h5" color="white">
                  {originalFileName || file.name}
                  <br />
                  {!!creativeType && `${creativeType.width}x${creativeType.height}`}
                </Typography>
              </Stack>
            </Stack>
          ))
        : creativesOverview?.map(({ url, originalFileName, creativeType, isPreviewInvalid }) => (
            <Stack key={url} height="100%" p={5}>
              <Stack alignItems="center" justifyContent="center" flexGrow={1}>
                {creativeType?.id?.toLowerCase().includes('video') ? (
                  isPreviewInvalid ? (
                    <NoPreview />
                  ) : (
                    <video width="85%" controls>
                      <source src={url} />
                    </video>
                  )
                ) : (
                  <img alt={originalFileName} src={url} />
                )}
              </Stack>
              <Stack textAlign="center">
                <Typography variant="h5" color="white">
                  {originalFileName}
                </Typography>
              </Stack>
            </Stack>
          ))}
    </Carousel>
  );

  useEffect(() => {
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        onNext();
      } else if (e.key === 'ArrowLeft') {
        onPrev();
      }
    }

    if (isOpened) {
      window.addEventListener('keyup', handleKeyUp);
    } else {
      window.removeEventListener('keyup', handleKeyUp);
    }

    return () => window.removeEventListener('keyup', handleKeyUp);
  }, [isOpened, onNext, onPrev]);

  return <Dialog size="lg" open={isOpened} title={title} content={content} onClose={onClose} showActions={false} />;
};
